import { useCallback, useEffect, useMemo, useState } from "react";
import { FormHelperText } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import {
  useUploadDocument,
  type BatchUploadSuccessData,
} from "@features/vessel-nomination/add/hooks/useUploadDocument.ts";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  DocumentTypeOptions,
  FIELDS_LANG_BASE,
  FORM_FIELDS,
  LANG_BASE,
  PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY,
  TEST_ID_BASE,
  type PreNominationRequestAttachment,
  type PreNominationRequestFormData,
} from "../constants";

const UPLOAD_STORE_KEY = PRE_NOMINATION_REQUEST_UPLOAD_STORE_KEY;
const MAX_FILES = 25;

const EMPTY_FILES: UploadEntry[] = [];

const getAttachmentField = {
  documentType: (index: number) => `${FORM_FIELDS.ATTACHMENTS}.${index}.documentType` as const,
  otherSpecify: (index: number) => `${FORM_FIELDS.ATTACHMENTS}.${index}.otherSpecify` as const,
  documentName: (index: number) => `${FORM_FIELDS.ATTACHMENTS}.${index}.documentName` as const,
  issueDate: (index: number) => `${FORM_FIELDS.ATTACHMENTS}.${index}.issueDate` as const,
  expirationDate: (index: number) => `${FORM_FIELDS.ATTACHMENTS}.${index}.expirationDate` as const,
  documentSummary: (index: number) =>
    `${FORM_FIELDS.ATTACHMENTS}.${index}.documentSummary` as const,
};

const isFileEntry = (entry: unknown): entry is File =>
  typeof File !== "undefined" && entry instanceof File;

const getEntryUrl = (entry?: UploadEntry) => {
  if (!entry || isFileEntry(entry)) {
    return "";
  }
  return typeof entry.url === "string" ? entry.url : "";
};

const getEntryName = (entry: UploadEntry | undefined, index: number) => {
  if (!entry) {
    return `document-${index + 1}`;
  }
  if (typeof entry.name === "string" && entry.name.trim()) {
    return entry.name;
  }
  return `document-${index + 1}`;
};

const getFileKey = (file: UploadEntry, index: number) => {
  const documentUrl = getEntryUrl(file);
  if (documentUrl) {
    return documentUrl;
  }
  if (isFileEntry(file)) {
    return [file.name, file.size, file.type, file.lastModified].join("|");
  }
  return `${getEntryName(file, index)}-${index}`;
};

interface PreNominationRequestDocumentUploadProps {
  updateFormData: <K extends keyof PreNominationRequestFormData>(
    key: K,
    value: PreNominationRequestFormData[K],
  ) => void;
  disabled?: boolean;
}

const PreNominationRequestDocumentUpload = ({
  updateFormData,
  disabled = false,
}: PreNominationRequestDocumentUploadProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();
  const { control, getValues, setValue } = useFormContext<PreNominationRequestFormData>();
  const [error, setError] = useState("");
  const files = useUploadStore((state) => state.filesByKey[UPLOAD_STORE_KEY] ?? EMPTY_FILES);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const removeFileAtIndex = useUploadStore((state) => state.removeFileAtIndex);
  const { remove } = useFieldArray({
    control,
    name: FORM_FIELDS.ATTACHMENTS,
  });
  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  useEffect(() => {
    const currentAttachments = [...(getValues(FORM_FIELDS.ATTACHMENTS) ?? [])];
    let hasChanges = false;
    files.forEach((file, index) => {
      const documentUrl = getEntryUrl(file);
      if (!documentUrl) {
        return;
      }
      const existingAttachment = currentAttachments[index];
      if (existingAttachment?.documentUrl === documentUrl) {
        return;
      }
      currentAttachments[index] = {
        documentUrl,
        documentName: existingAttachment?.documentName || getEntryName(file, index),
        documentType: existingAttachment?.documentType ?? "",
        otherSpecify: existingAttachment?.otherSpecify ?? "",
        issueDate: existingAttachment?.issueDate ?? null,
        expirationDate: existingAttachment?.expirationDate ?? null,
        documentSummary: existingAttachment?.documentSummary ?? "",
      } satisfies PreNominationRequestAttachment;
      hasChanges = true;
    });
    if (!hasChanges) {
      return;
    }
    setValue(FORM_FIELDS.ATTACHMENTS, currentAttachments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(FORM_FIELDS.ATTACHMENTS, currentAttachments);
  }, [files, getValues, setValue, updateFormData]);
  const handleUploadSuccess = useCallback(
    ({ documents, startIdx }: BatchUploadSuccessData) => {
      const currentAttachments = [...(getValues(FORM_FIELDS.ATTACHMENTS) ?? [])];
      updateFilesForKey(UPLOAD_STORE_KEY, (previousFiles) => {
        const nextFiles = [...previousFiles];
        documents.forEach(({ documentUrl, documentName }, fileOffset) => {
          const index = startIdx + fileOffset;
          const existingAttachment = currentAttachments[index];
          const uploadedDocumentName = documentName || `document-${index + 1}`;
          nextFiles[index] = {
            name: uploadedDocumentName,
            url: documentUrl,
          };
          currentAttachments[index] = {
            documentUrl,
            documentName: existingAttachment?.documentName || uploadedDocumentName,
            documentType: existingAttachment?.documentType ?? "",
            otherSpecify: existingAttachment?.otherSpecify ?? "",
            issueDate: existingAttachment?.issueDate ?? null,
            expirationDate: existingAttachment?.expirationDate ?? null,
            documentSummary: existingAttachment?.documentSummary ?? "",
          };
        });
        return nextFiles;
      });
      setValue(FORM_FIELDS.ATTACHMENTS, currentAttachments, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(FORM_FIELDS.ATTACHMENTS, currentAttachments);
    },
    [getValues, setValue, updateFilesForKey, updateFormData],
  );
  const handleUploadError = useCallback(
    (
      uploadError: Error,
      metadata: {
        startIdx?: number;
        count: number;
      },
    ) => {
      notify({
        title: t(`${LANG_BASE}.notifications.additional-documents-upload-error-title`),
        description: t(`${LANG_BASE}.notifications.additional-documents-upload-error-description`),
        type: "error",
      });
      console.error("Error uploading pre-nomination request documents:", uploadError);
      const startIndex = metadata.startIdx ?? 0;
      Array.from({ length: metadata.count }, (_, index) => startIndex + index)
        .reverse()
        .forEach((index) => {
          removeFileAtIndex(UPLOAD_STORE_KEY, index);
          remove(index);
        });
      const currentAttachments = getValues(FORM_FIELDS.ATTACHMENTS) ?? [];
      updateFormData(FORM_FIELDS.ATTACHMENTS, currentAttachments);
    },
    [getValues, notify, remove, removeFileAtIndex, t, updateFormData],
  );

  const uploadDocuments = useUploadDocument({
    multiple: true,
    handleUploadSuccess,
    handleUploadError,
  });

  const handleUpload = useCallback(
    (incomingFiles: File[]) => {
      setError("");
      if (!incomingFiles.length) {
        return;
      }
      const currentAttachments = getValues(FORM_FIELDS.ATTACHMENTS) ?? [];
      uploadDocuments.mutate({
        files: incomingFiles,
        documentType: "PRE_NOMINATION_REQUEST_DOCUMENT",
        startIdx: currentAttachments.length,
      });
    },
    [getValues, uploadDocuments],
  );

  const handleReject = useCallback(() => {
    const allowedTypes = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");
    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: allowedTypes,
      }),
    );
  }, [t, uploadConditions.format, uploadConditions.maxSizeLabel]);

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount = useUploadStore.getState().getFilesForKey(UPLOAD_STORE_KEY).length;
      if (existingFilesCount + incomingFiles.length > MAX_FILES) {
        return t("form-validation.max-files", {
          max: MAX_FILES,
        });
      }
      return null;
    },
    [t],
  );

  const handleValidationResult = useCallback((message: string | null) => {
    setError(message ?? "");
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      const nextUploadFiles = files.filter((_, fileIndex) => fileIndex !== index);
      const currentAttachments = [...(getValues(FORM_FIELDS.ATTACHMENTS) ?? [])];
      const nextAttachments = currentAttachments.filter(
        (_, attachmentIndex) => attachmentIndex !== index,
      );
      setFilesForKey(UPLOAD_STORE_KEY, nextUploadFiles);
      remove(index);
      setValue(FORM_FIELDS.ATTACHMENTS, nextAttachments, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData(FORM_FIELDS.ATTACHMENTS, nextAttachments);
    },
    [files, getValues, remove, setFilesForKey, setValue, updateFormData],
  );

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      setValue(key as Parameters<typeof setValue>[0], value as Parameters<typeof setValue>[1], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      const currentAttachments = getValues(FORM_FIELDS.ATTACHMENTS) ?? [];
      updateFormData(FORM_FIELDS.ATTACHMENTS, currentAttachments);
    },
    [getValues, setValue, updateFormData],
  );
  const isUploading = uploadDocuments.isPending;
  const isMaxFilesReached = files.length >= MAX_FILES;
  return (
    <div className="flex flex-col gap-4" data-testid={`${TEST_ID_BASE}-documents`}>
      <FileUpload
        testId={`${TEST_ID_BASE}-documents-upload`}
        name={UPLOAD_STORE_KEY}
        conditions={uploadConditions}
        files={files}
        setFiles={(nextFiles) => setFilesForKey(UPLOAD_STORE_KEY, nextFiles.slice(0, MAX_FILES))}
        maxFiles={MAX_FILES}
        allowedExtensions={allowedExtensions}
        onUpload={handleUpload}
        onReject={handleReject}
        disabled={disabled || isUploading || isMaxFilesReached}
        uploadDescription={t("upload.additional-documents-upload", {
          extensions: uploadConditions.format?.join(", "),
          size: uploadConditions.maxSizeLabel,
        })}
        validateDrop={(selectedCount) => {
          if (selectedCount > MAX_FILES) {
            return t("form-validation.max-files", {
              max: MAX_FILES,
            });
          }

          return null;
        }}
        validateFiles={validateUploadFiles}
        onValidationResult={handleValidationResult}
      />
      {error && (
        <FormHelperText
          sx={{
            ml: 0,
            my: "4px",
            minHeight: "20px",
            fontSize: 12,
            fontWeight: 500,
            color: "error.main",
          }}
        >
          {error}
        </FormHelperText>
      )}
      {files.map((file, index) => {
        const currentAttachment = getValues(FORM_FIELDS.ATTACHMENTS)?.[index];
        const hasDocumentUrl = !!currentAttachment?.documentUrl;
        const isCurrentFileLoading = isUploading && !hasDocumentUrl && isFileEntry(file);
        const showOtherDocumentType = currentAttachment?.documentType === "OTHERS_SPECIFY";
        const isCertificate = currentAttachment?.documentType === "CERTIFICATE";
        const documentTestId = `${TEST_ID_BASE}-documents-${index}`;
        return (
          <div className="flex flex-col gap-3" key={getFileKey(file, index)}>
            <div data-testid={`${documentTestId}-single-file`}>
              <SingleFile
                testId={`${documentTestId}-file`}
                file={file}
                fieldName={UPLOAD_STORE_KEY}
                currentFileIndex={index}
                setFiles={() => undefined}
                removeFn={() => handleRemoveFile(index)}
                isLoading={isCurrentFileLoading}
                disabled={disabled || isUploading}
              />
            </div>
            {hasDocumentUrl && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormItem<PreNominationRequestFormData>
                  testId={`${documentTestId}-document-type`}
                  name={getAttachmentField.documentType(index)}
                  type="select"
                  label={t(`${FIELDS_LANG_BASE}.document-type`)}
                  options={DocumentTypeOptions}
                  onFieldChange={handleFieldChange}
                  disabled={disabled}
                  required
                />
                {showOtherDocumentType ? (
                  <FormItem<PreNominationRequestFormData>
                    testId={`${documentTestId}-other-specify`}
                    name={getAttachmentField.otherSpecify(index)}
                    type="text"
                    maxChars={30}
                    label={t(`${FIELDS_LANG_BASE}.other-specify`)}
                    onFieldChange={handleFieldChange}
                    disabled={disabled}
                    required
                  />
                ) : (
                  <div className="hidden md:block" aria-hidden="true" />
                )}
                <div className="md:col-span-2">
                  <FormItem<PreNominationRequestFormData>
                    testId={`${documentTestId}-document-name`}
                    name={getAttachmentField.documentName(index)}
                    type="text"
                    maxChars={50}
                    label={t(`${FIELDS_LANG_BASE}.document-name`)}
                    onFieldChange={handleFieldChange}
                    disabled={disabled}
                  />
                </div>
                <FormItem<PreNominationRequestFormData>
                  testId={`${documentTestId}-issue-date`}
                  name={getAttachmentField.issueDate(index)}
                  type="date-picker"
                  label={t(`${FIELDS_LANG_BASE}.issue-date`)}
                  onFieldChange={handleFieldChange}
                  timePicker={false}
                  dateConstraint="not-future"
                  disabled={disabled}
                />
                <FormItem<PreNominationRequestFormData>
                  testId={`${documentTestId}-expiration-date`}
                  name={getAttachmentField.expirationDate(index)}
                  type="date-picker"
                  label={t(`${FIELDS_LANG_BASE}.expiration-date`)}
                  onFieldChange={handleFieldChange}
                  timePicker={false}
                  dateConstraint="not-past"
                  disabled={disabled}
                  required={isCertificate}
                />
                <div className="md:col-span-2">
                  <FormItem<PreNominationRequestFormData>
                    testId={`${documentTestId}-document-summary`}
                    name={getAttachmentField.documentSummary(index)}
                    type="text"
                    maxChars={300}
                    label={t(`${FIELDS_LANG_BASE}.document-summary`)}
                    onFieldChange={handleFieldChange}
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PreNominationRequestDocumentUpload;
